import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, IconButton, SelectPicker, Whisper, Tooltip, toaster, Message, Divider, SortType } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import RunningClubMemberDrawer from './running-club-member-drawer';
import { ClubMemberRoleList, ClubMembershipStatusList, RunningClubMember, RunningClubMemberPaginationFilterRequest } from '@/models/running-club-member.model';
import { deleteRunningClubMember, getPagedRunningClubMembers } from '@/services/running-club-member.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getRunningClubsLookup } from '@/services/running-club.service';
import { getUsersLookup } from '@/services/user.service';
import { RoleEnum } from '@/models/user.model';
import { Icon } from '@rsuite/icons';
import { MdDelete } from 'react-icons/md';
import PageContent from '@/components/PageContent';
import ImageCell from '@/components/ImageCell';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

/** A retired or unknown enum value renders as its raw number rather than blank. */
const roleLabel = (role: number) => ClubMemberRoleList.find(r => r.value === role)?.label ?? `${role}`;
const statusLabel = (status: number) => ClubMembershipStatusList.find(s => s.value === status)?.label ?? `${status}`;

export interface RunningClubMemberListProps {
  runningClubId?: number;
  userId?: number;
}

const RunningClubMemberList : React.FC<RunningClubMemberListProps> = ({runningClubId, userId}) => {
  const [filterReq, setFilterReq] = useState<RunningClubMemberPaginationFilterRequest>(new RunningClubMemberPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunningClubMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemToEdit, setItemToEdit] = useState<RunningClubMember|undefined>(undefined);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: RunningClubMemberPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runningClubId: runningClubId ?? req.runningClubId,
        userId: userId ?? req.userId,
      };
      const result: PagedResponseModel<RunningClubMember> = await getPagedRunningClubMembers(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Running Club Members:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setItemToEdit(undefined);
    fetchData(filterReq);
  };

  const onEditItem = (rowData: RunningClubMember)=>{
    setItemToEdit(rowData);
    handleOpenDrawer();
  };

  const onDeleteItem = (id: number, title?: string) => {

    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result: BaseResponseModel<null> = await deleteRunningClubMember(id);
        if (result.success) {
          toastSuccessMessage(`Membership Removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting Membership:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear();
      }
    };

    const handleNoClick = () => {
      toaster.clear();
    };

    toaster.push(
      <Message type="error" closable>
        <p style={{marginBottom:10}}>Are you sure you want to remove <strong>{title ?? 'Membership'}</strong>?</p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        {!userId && <Button appearance="primary" onClick={handleOpenDrawer}>
          Add Member
        </Button>}

        <Stack spacing={6}>
          {!runningClubId && <FetchingSelect
            label="Running Club"
            fetchData={(text, page) => getRunningClubsLookup(text, page)}
            value={filterReq.runningClubId}
            onChange={dataKey => setFilterReq(pre => ({...pre, runningClubId: dataKey}))}
          />}
          {!userId && <FetchingSelect
            label="User"
            fetchData={(text, page) => getUsersLookup(text, RoleEnum.User, page)}
            value={filterReq.userId}
            onChange={dataKey => setFilterReq(pre => ({...pre, userId: dataKey}))}
          />}
          <SelectPicker
            label="Role"
            data={ClubMemberRoleList}
            searchable={false}
            value={filterReq.role ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, role: dataKey ?? undefined }))}
          />
          <SelectPicker
            label="Status"
            data={ClubMembershipStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, status: dataKey ?? undefined }))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        {!userId && <Column flexGrow={0.5} align="center" fullText sortable>
          <HeaderCell>Profile</HeaderCell>
          <ImageCell dataKey="user.profilePicture" />
        </Column>}
        {!userId && <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>User</HeaderCell>
          <Cell>
            {(rowData: RunningClubMember) => `${rowData.user.firstName} ${rowData.user.lastName}`}
          </Cell>        
        </Column>}
        {!runningClubId && <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Running Club</HeaderCell>
          <Cell dataKey="runningClub.name" />
        </Column>}
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Role</HeaderCell>
          <Cell dataKey="role">
            {(rowData: RunningClubMember) => roleLabel(rowData.role)}
          </Cell>
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Status</HeaderCell>
          <Cell dataKey="status">
            {(rowData: RunningClubMember) => statusLabel(rowData.status)}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" fixed="right">
          <HeaderCell>...</HeaderCell>
            <Cell style={{ padding: '6px' }}>
              {(rowData: RunningClubMember) => (
              <Stack spacing={16}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Edit Membership</Tooltip>}>
                  <IconButton onClick={() => onEditItem(rowData)} icon={<EditIcon />} circle size="sm" />
                </Whisper>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Delete Membership</Tooltip>}>
                  <IconButton circle size="sm" color="red" appearance="ghost"
                    onClick={()=> onDeleteItem(
                      rowData.id,
                      `${rowData.user?.firstName} ${rowData.user?.lastName} from ${rowData.runningClub?.name} as ${roleLabel(rowData.role)}`
                    )}
                    icon={<Icon as={MdDelete}/>} />
                </Whisper>
              </Stack>
              )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

      <RunningClubMemberDrawer
        value={itemToEdit}
        runningClubId={runningClubId}
        userId={userId}
        open={openDrawer}
        onClose={handleCloseDrawer}
      />
    </PageContent>
  );
};

export default RunningClubMemberList;
